import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  Switch,
  TextField,
} from '@mui/material';
import useApi from '../../hooks/useApi';
import { useErrorToast } from '../../components/common';
import { getApiErrorMessage } from '../../utils/errorMessages';
import { createAiModel, updateAiModel } from '../../services/aiGovernanceService';

const MAX_PRICE_PER_MILLION = 100000;

const emptyValues = () => ({
  modelCode: '',
  modelName: '',
  provider: '',
  description: '',
  inputPricePerMillion: '',
  outputPricePerMillion: '',
  displayOrder: '',
  isActive: true,
});

const valuesFromModel = (model) => ({
  modelCode: model.modelCode || '',
  modelName: model.modelName || '',
  provider: model.provider || '',
  description: model.description || '',
  inputPricePerMillion:
    model.inputPricePerMillion === null || model.inputPricePerMillion === undefined
      ? ''
      : String(model.inputPricePerMillion),
  outputPricePerMillion:
    model.outputPricePerMillion === null || model.outputPricePerMillion === undefined
      ? ''
      : String(model.outputPricePerMillion),
  displayOrder: model.displayOrder === null || model.displayOrder === undefined ? '' : String(model.displayOrder),
  isActive: model.isActive !== false,
});

/**
 * Add or edit an AI model and its price.
 *
 * The model code is the join key between the UI, the backend and the usage
 * ledger, so it is immutable once a model exists — renaming it would orphan
 * every usage row recorded against it.
 */
const AiModelFormDialog = ({ open, model = null, onClose, onSaved }) => {
  const api = useApi();
  const { showErrorToast } = useErrorToast();
  const isEditMode = !!model;

  const [values, setValues] = useState(emptyValues());
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValues(model ? valuesFromModel(model) : emptyValues());
    setErrors({});
  }, [model, open]);

  const handleChange = (field) => (event) => {
    const value = field === 'isActive' ? event.target.checked : event.target.value;
    setValues((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleCodeChange = (event) => {
    // Codes are uppercase snake_case to match the ids the backend ships with
    const normalized = event.target.value
      .toUpperCase()
      .replace(/\s+/g, '_')
      .replace(/[^A-Z0-9_]/g, '');
    setValues((prev) => ({ ...prev, modelCode: normalized }));
    if (errors.modelCode) {
      setErrors((prev) => ({ ...prev, modelCode: undefined }));
    }
  };

  const validatePrice = (raw, label) => {
    if (raw === '' || raw === null || raw === undefined) {
      return `${label} is required`;
    }
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return `${label} must be a number`;
    if (parsed < 0) return `${label} cannot be negative`;
    if (parsed > MAX_PRICE_PER_MILLION) return `${label} looks too large`;
    return null;
  };

  const validate = () => {
    const next = {};

    if (!isEditMode) {
      if (!values.modelCode.trim()) {
        next.modelCode = 'Model code is required';
      } else if (!/^[A-Z0-9_]+$/.test(values.modelCode.trim())) {
        next.modelCode = 'Use uppercase letters, numbers and underscores only';
      }
    }

    if (!values.modelName.trim()) {
      next.modelName = 'Display name is required';
    }

    const inputPriceError = validatePrice(values.inputPricePerMillion, 'Input price');
    if (inputPriceError) next.inputPricePerMillion = inputPriceError;

    const outputPriceError = validatePrice(values.outputPricePerMillion, 'Output price');
    if (outputPriceError) next.outputPricePerMillion = outputPriceError;

    if (values.displayOrder !== '' && !Number.isInteger(Number(values.displayOrder))) {
      next.displayOrder = 'Display order must be a whole number';
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setSaving(true);
    try {
      const payload = {
        modelName: values.modelName.trim(),
        provider: values.provider.trim() || null,
        description: values.description.trim() || null,
        inputPricePerMillion: Number(values.inputPricePerMillion),
        outputPricePerMillion: Number(values.outputPricePerMillion),
        isActive: values.isActive,
      };
      if (values.displayOrder !== '') {
        payload.displayOrder = Number(values.displayOrder);
      }

      if (isEditMode) {
        await updateAiModel(api, model.modelCode, payload);
      } else {
        await createAiModel(api, { ...payload, modelCode: values.modelCode.trim() });
      }

      onSaved?.();
    } catch (err) {
      console.error('Error saving AI model:', err);
      showErrorToast(getApiErrorMessage(err, `Failed to ${isEditMode ? 'update' : 'create'} AI model`));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>
        {isEditMode ? `Edit ${model.modelName}` : 'Add AI Model'}
      </DialogTitle>

      <DialogContent dividers>
        {isEditMode && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Changing a price opens a new rate from now on. Usage already recorded keeps the price it
            was billed at.
          </Alert>
        )}

        <TextField
          autoFocus={!isEditMode}
          margin="dense"
          label="Model Code"
          fullWidth
          variant="outlined"
          value={values.modelCode}
          onChange={handleCodeChange}
          error={!!errors.modelCode}
          helperText={
            errors.modelCode ||
            (isEditMode
              ? 'The identifier usage is recorded against — cannot be changed'
              : 'Must match the id the AI gateway is configured with, e.g. CLAUDE_4_5_SONNET')
          }
          disabled={isEditMode}
          InputProps={{ readOnly: isEditMode }}
          sx={{ mb: 2 }}
        />

        <TextField
          autoFocus={isEditMode}
          margin="dense"
          label="Display Name"
          fullWidth
          variant="outlined"
          value={values.modelName}
          onChange={handleChange('modelName')}
          error={!!errors.modelName}
          helperText={errors.modelName || 'Shown in model pickers, e.g. Claude 4.5 Sonnet'}
          sx={{ mb: 2 }}
        />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
            mb: 2,
          }}
        >
          <TextField
            label="Provider"
            fullWidth
            variant="outlined"
            value={values.provider}
            onChange={handleChange('provider')}
            helperText="e.g. Anthropic, OpenAI"
          />
          <TextField
            label="Display Order"
            fullWidth
            variant="outlined"
            value={values.displayOrder}
            onChange={handleChange('displayOrder')}
            error={!!errors.displayOrder}
            helperText={errors.displayOrder || 'Lower appears first'}
            inputProps={{ inputMode: 'numeric' }}
          />
          <TextField
            label="Input Price"
            fullWidth
            variant="outlined"
            value={values.inputPricePerMillion}
            onChange={handleChange('inputPricePerMillion')}
            error={!!errors.inputPricePerMillion}
            helperText={errors.inputPricePerMillion || 'Per 1M input tokens'}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
              endAdornment: <InputAdornment position="end">/ 1M</InputAdornment>,
            }}
            inputProps={{ inputMode: 'decimal' }}
          />
          <TextField
            label="Output Price"
            fullWidth
            variant="outlined"
            value={values.outputPricePerMillion}
            onChange={handleChange('outputPricePerMillion')}
            error={!!errors.outputPricePerMillion}
            helperText={errors.outputPricePerMillion || 'Per 1M output tokens'}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
              endAdornment: <InputAdornment position="end">/ 1M</InputAdornment>,
            }}
            inputProps={{ inputMode: 'decimal' }}
          />
        </Box>

        <TextField
          label="Description"
          fullWidth
          multiline
          minRows={2}
          variant="outlined"
          value={values.description}
          onChange={handleChange('description')}
          sx={{ mb: 1 }}
        />

        <FormControlLabel
          control={<Switch checked={values.isActive} onChange={handleChange('isActive')} />}
          label={values.isActive ? 'Available to users' : 'Hidden from model pickers'}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving} color="secondary">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving}
          sx={{
            backgroundColor: '#000000',
            '&:hover': { backgroundColor: '#333333' },
          }}
        >
          {saving ? <CircularProgress size={24} /> : isEditMode ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AiModelFormDialog;
